// The clock of a time zone (IANA, e.g. `Europe/Rome`): the instant at which it shows a day and a time, and what it
// shows at an instant. Days are `YYYY-MM-DD` and times `HH:mm`: they compare as text. The showrooms of booking and the
// schedules of the allocation of sales read them (ADR-089, ADR-186).

const two = (value: number) => String(value).padStart(2, "0");

/** What a clock of the time zone shows at an instant. */
function clockOf(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  return {
    year: part("year"),
    month: part("month"),
    day: part("day"),
    hour: part("hour"),
    minute: part("minute"),
    second: part("second"),
  };
}

/** How far ahead of UTC the time zone is at an instant, in milliseconds. */
function offsetOf(instant: number, timeZone: string): number {
  const c = clockOf(new Date(instant), timeZone);
  return Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second) - Math.floor(instant / 1000) * 1000;
}

/**
 * The instant at which the clocks of a time zone show a day and a time. The offset is looked up twice: the first
 * guess can fall on the other side of a change of the clocks.
 */
export function zonedInstant(day: string, time: string, timeZone: string): Date {
  const wall = Date.parse(`${day}T${time}:00Z`);
  const guess = wall - offsetOf(wall, timeZone);
  return new Date(wall - offsetOf(guess, timeZone));
}

/** `HH:mm` of a number of minutes after midnight: 75 is `01:15`. */
export const timeOfMinutes = (minutes: number): string => `${two(Math.floor(minutes / 60))}:${two(minutes % 60)}`;

/** The day and the time the clocks of a time zone show at an instant. */
export function zonedTime(instant: Date, timeZone: string): { date: string; time: string } {
  const c = clockOf(instant, timeZone);
  return { date: `${c.year}-${two(c.month)}-${two(c.day)}`, time: `${two(c.hour)}:${two(c.minute)}` };
}
